import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Alert, Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import { TestShackReportFilters } from "../../shared/components/report/TestShackReportFilters";
import { M1TestShackReportImageDialog } from "./components/M1TestShackReportImageDialog";
import { M1TestShackReportSectionRevList } from "./components/M1TestShackReportSectionRevList";
import { useTestShackReportData } from "../../hooks/useTestShackReportData";
import type {
	TestShackReportFilterOptions,
	TestShackReportInspection,
} from "../../models/TestShackReport";
import { groupTestShackReportBySectionRev } from "./utils/groupBySectionRev";

const EMPTY_FILTER_OPTIONS: TestShackReportFilterOptions = {
	sections: [],
	events: [],
	heats: [],
};

export const M1TestShackReportPage = () => {
	const [imageTarget, setImageTarget] =
		useState<TestShackReportInspection | null>(null);
	const {
		error,
		clearError,
		handleError,
		filters,
		handleFilterChange,
		filterOptionsQuery,
		reportQuery,
		inspections,
		isLoading,
		isQueryError,
	} = useTestShackReportData(MILL_1.id);
	const sectionRevs = groupTestShackReportBySectionRev(inspections);

	return (
		<>
			<HelmetDexteel title="Test Shack Report" />
			<Box sx={{ p: 2 }}>
				<Stack spacing={1.5}>
					<Typography variant="h5" fontWeight={800}>
						Test Shack Report
					</Typography>

					<TestShackReportFilters
						assetId={MILL_1.id}
						filters={filters}
						options={filterOptionsQuery.data ?? EMPTY_FILTER_OPTIONS}
						isLoadingOptions={filterOptionsQuery.isLoading}
						onChange={handleFilterChange}
					/>

					{isLoading && (
						<LinearProgress aria-label="Loading Test Shack report" />
					)}
					{isQueryError && (
						<Alert severity="error" role="alert">
							The Test Shack report could not be loaded. Review the error and
							retry the selected range.
						</Alert>
					)}
					{!isLoading &&
						!isQueryError &&
						reportQuery.data &&
						inspections.length === 0 && (
							<Alert severity="info" role="status">
								No inspections match the selected report filters.
							</Alert>
						)}

					<M1TestShackReportSectionRevList
						sectionRevs={sectionRevs}
						inspections={inspections}
						isLoading={isLoading}
						problemsOnly={filters.problemsOnly}
						showMinTargetMax={filters.showMinTargetMax}
						onProblemsOnlyChange={(problemsOnly) =>
							handleFilterChange({ problemsOnly })
						}
						onShowMinTargetMaxChange={(showMinTargetMax) =>
							handleFilterChange({ showMinTargetMax })
						}
						onViewImages={setImageTarget}
						onError={handleError}
					/>
				</Stack>
			</Box>
			{imageTarget && (
				<M1TestShackReportImageDialog
					key={imageTarget.ProfileInspectionId}
					inspection={imageTarget}
					onClose={() => setImageTarget(null)}
				/>
			)}
			<ErrorModal error={error} onHide={clearError} />
		</>
	);
};
